import { useNavigate } from 'react-router-dom';
import { 
    Trash2, 
    Target,
    Calendar,
    Clock,
    ArrowRight
} from 'lucide-react';

type GoalEntryProps = {
    goal: any;
    remove: (id: string) => void;
};

const progressColors = {
    red: '#ef4444',
    amber: '#f59e0b',
    green: '#10b981',
};

const progressLabels = {
    red: 'Behind',
    amber: 'On Track',
    green: 'Ahead',
};

const GoalEntry: React.FC<GoalEntryProps> = ({ goal, remove }) => {
    const navigate = useNavigate();

    const getProgressConfig = (rag: string) => {
        switch(rag) {
            case 'red':
                return {
                    color: 'from-red-500 to-red-600',
                    bgColor: 'bg-red-50',
                    textColor: 'text-red-700',
                    dotColor: progressColors.red,
                    label: progressLabels.red
                };
            case 'amber':
                return {
                    color: 'from-amber-500 to-amber-600',
                    bgColor: 'bg-amber-50',
                    textColor: 'text-amber-700',
                    dotColor: progressColors.amber,
                    label: progressLabels.amber
                };
            case 'green':
                return {
                    color: 'from-green-500 to-green-600',
                    bgColor: 'bg-green-50',
                    textColor: 'text-green-700',
                    dotColor: progressColors.green,
                    label: progressLabels.green
                };
            default:
                return {
                    color: 'from-gray-500 to-gray-600',
                    bgColor: 'bg-gray-50',
                    textColor: 'text-gray-700',
                    dotColor: '#6b7280',
                    label: 'Not Set'
                };
        }
    };

    const progressConfig = getProgressConfig(goal.rag);

    const handleParentClick = () => {
        navigate("/goal", { state: { goal }});
    };

    const handleDeleteClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        remove(goal.id);
    };

    return (
        <div 
            className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-xl mb-4 cursor-pointer group"
            onClick={handleParentClick}
        >
            {/* Header */}
            <div className={`bg-gradient-to-r ${progressConfig.color} p-4 text-white relative`}>
                <div className="absolute inset-0 bg-white opacity-10"></div>
                <div className="relative flex items-center justify-between">
                    <div className="flex items-center space-x-3 text-black">
                        <Target className="w-4 h-4" />
                        <div>
                            <h3 className="font-bold text-lg">
                                {goal.title.length > 55 ? goal.title.slice(0, 55) + '...' : goal.title}
                            </h3>
                            {goal.rag && (
                                <span className="text-sm bg-white bg-opacity-20 px-2 py-1 rounded-full" style={{color: 'black'}}>
                                    {goal.goal_type}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={handleDeleteClick}
                            className="w-8 h-8 bg-red-500 bg-opacity-80 hover:bg-opacity-100 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                            title="Delete"
                        >
                            <Trash2 className="w-4 h-4 text-white" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="p-6">
                {/* Goal Title */}
                {/* <div className="mb-4">
                    <h4 className="text-xl font-semibold text-gray-800 leading-tight">
                        {goal.title.length > 55 ? goal.title.slice(0, 55) + '...' : goal.title}
                    </h4>
                </div> */}

                {/* Goal Details */}
                <div className="space-y-4">
                    {/* Dates */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="bg-blue-50 rounded-lg p-4 flex items-center space-x-3">
                            <Calendar className="w-5 h-5 text-blue-500" />
                            <div>
                                <div className="text-sm text-blue-600 font-medium">Target Date</div>
                                <div className="text-lg font-bold text-blue-800">
                                    {new Date(goal.target_date).toLocaleDateString('en-GB')}
                                </div>
                            </div>
                        </div>

                        <div className="bg-purple-50 rounded-lg p-4 flex items-center space-x-3">
                            <Clock className="w-5 h-5 text-purple-500" />
                            <div>
                                <div className="text-sm text-purple-600 font-medium">Next Review</div>
                                <div className="text-lg font-bold text-purple-800">
                                    {new Date(goal.next_review_date).toLocaleDateString('en-GB')}
                                </div>
                            </div>
                        </div>

                        {/* Progress */}
                        {goal.rag && (
                            <div className={`${progressConfig.bgColor} rounded-lg p-4 flex items-center space-x-3`}>
                                <div 
                                    className="w-4 h-4 rounded-full" 
                                    style={{ backgroundColor: progressConfig.dotColor }}
                                ></div>
                                <div>
                                    <div className={`text-sm font-medium ${progressConfig.textColor.replace('text-', 'text-').replace('-700', '-600')}`}>
                                        Progress Status
                                    </div>
                                    <div className={`text-lg font-bold ${progressConfig.textColor}`}>
                                        {progressConfig.label}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Click indicator */}
                <div className="flex items-center justify-center mt-4 pt-4 border-t border-gray-100 text-gray-500 group-hover:text-gray-700 transition-colors duration-200">
                    <span className="text-sm font-medium mr-2">View Details</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                </div>
            </div>
        </div>
    );
};

export default GoalEntry;